import { recalcItem, variantToOrderItem } from './orderItemCalc';
import { mapDetailToPosVariant } from './mapVariantDetail';
import { mergeLooseDuplicates } from './mergeLooseDuplicates';
import type { PosComboDefinition } from './comboMatcher';
import type { FulfillmentMode, OrderItem, PosVariantDetailItem } from '../types';

/** Số lượng còn thiếu của MỘT thành phần để cụm lên tới `nextCount` combo. */
export interface ComboDeficit {
  variantId: string;
  /** Mã dùng để gọi `posService.lookupVariants`. */
  variantCode: string;
  /** SL của thành phần này trong MỘT combo. */
  comboQty: number;
  /** Số đơn vị phải thêm vào giỏ. Luôn ≥ 1 (thành phần không thiếu bị loại). */
  deficit: number;
}

function qtyInGroup(items: OrderItem[], groupId: string, variantId: string): number {
  return items
    .filter((item) => item.comboGroupId === groupId && item.variantId === variantId)
    .reduce((acc, item) => acc + item.quantity, 0);
}

function qtyLoose(items: OrderItem[], variantId: string): number {
  return items
    .filter((item) => !item.comboGroupId && item.variantId === variantId)
    .reduce((acc, item) => acc + item.quantity, 0);
}

/** SL của biến thể đang nằm trong cụm combo KHÁC — `setComboGroupCount` không đụng tới. */
function qtyInOtherGroups(items: OrderItem[], groupId: string, variantId: string): number {
  return items
    .filter(
      (item) =>
        item.variantId === variantId && !!item.comboGroupId && item.comboGroupId !== groupId,
    )
    .reduce((acc, item) => acc + item.quantity, 0);
}

/**
 * Mã biến thể để lookup tồn. Ưu tiên dòng đang có trong giỏ (`variantCode` đã
 * được `variantToOrderItem` điền); fallback `sku` của định nghĩa combo.
 */
function resolveVariantCode(
  items: OrderItem[],
  variantId: string,
  fallbackSku: string,
): string {
  return items.find((item) => item.variantId === variantId)?.variantCode ?? fallbackSku;
}

/**
 * TCR-982: hàng còn THIẾU trong giỏ để cụm `comboGroupId` lên tới `nextCount` combo.
 *
 * Rỗng nghĩa là giỏ đã đủ hàng — gọi thẳng `setComboGroupCount`, không cần mạng.
 */
export function planComboGrowth(
  items: OrderItem[],
  comboGroupId: string,
  nextCount: number,
  definition: PosComboDefinition,
): ComboDeficit[] {
  const deficits: ComboDeficit[] = [];
  for (const component of definition.component_items) {
    const have =
      qtyInGroup(items, comboGroupId, component.variant_id) +
      qtyLoose(items, component.variant_id);
    const deficit = component.qty * nextCount - have;
    if (deficit <= 0) continue;
    deficits.push({
      variantId: component.variant_id,
      variantCode: resolveVariantCode(items, component.variant_id, component.sku),
      comboQty: component.qty,
      deficit,
    });
  }
  return deficits;
}

/**
 * Số combo tối đa cụm có thể lên tới khi ĐƯỢC PHÉP thêm hàng vào giỏ, chặn trên
 * bởi tồn khả dụng tại chi nhánh.
 *
 * Suy ra từ ràng buộc "tổng SL biến thể v trong giỏ sau khi đổi ≤ tồn của v":
 *
 *   trongCụm + hàngLẻ + phảiThêm ≤ tồn − trongCụmKhác
 *   ⇔ comboQty_v × count ≤ tồn_v − trongCụmKhác_v
 *   ⇔ count ≤ ⌊(tồn_v − trongCụmKhác_v) / comboQty_v⌋
 *
 * `stockByVariantId` thiếu một biến thể (lookup không trả) → coi như tồn 0, trần
 * về 0: thà chặn còn hơn hứa một số combo mà kho không có hàng.
 */
export function maxComboCountByStock(
  items: OrderItem[],
  comboGroupId: string,
  definition: PosComboDefinition,
  stockByVariantId: Map<string, number>,
): number {
  return definition.component_items.reduce((acc, component) => {
    const stock = stockByVariantId.get(component.variant_id) ?? 0;
    const locked = qtyInOtherGroups(items, comboGroupId, component.variant_id);
    return Math.min(acc, Math.floor((stock - locked) / component.qty));
  }, Number.POSITIVE_INFINITY);
}

/**
 * `warehouseId` combo đang tăng số đã dùng cho biến thể này, nếu có — dòng bù
 * mới phải kế thừa, không được để `undefined` (khác khoá gộp `mergeLooseDuplicates`
 * với dòng component gốc, và cụm kết thúc với 2 kho khác nhau ngầm định).
 */
function resolveWarehouseId(
  items: OrderItem[],
  comboGroupId: string,
  variantId: string,
): string | undefined {
  return items.find(
    (item) => item.comboGroupId === comboGroupId && item.variantId === variantId,
  )?.warehouseId;
}

/**
 * Thêm phần thiếu vào giỏ dưới dạng dòng LẺ, để `setComboGroupCount` rút vào cụm
 * ở bước sau.
 *
 * Dòng mới dựng qua `variantToOrderItem` + `mapDetailToPosVariant` — đúng đường
 * của luồng thêm SP thường (giống `addComboToCart`), nếu không sẽ thiếu âm thầm
 * VAT / kho / brand / snapshot giá vàng. Dựng xong gộp ngay vào dòng lẻ sẵn có
 * cùng biến thể (`mergeLooseDuplicates`) để không đẻ ra đúng cái lỗi TCR-983.
 *
 * Ném lỗi khi lookup không trả chi tiết của một biến thể — cùng khuôn với
 * `addComboToCart`, người gọi đã bọc try/catch.
 *
 * `comboGroupId`: cụm đang tăng số — dùng để tra `warehouseId` đã chọn sẵn của
 * component cùng biến thể, kế thừa cho dòng bù (TCR-982 regression).
 */
export function addComboDeficitLines(
  items: OrderItem[],
  deficits: ComboDeficit[],
  details: PosVariantDetailItem[],
  fulfillmentMode: FulfillmentMode,
  newId: () => string,
  comboGroupId: string,
): OrderItem[] {
  // Tra theo `variant_code` — đúng khoá đã gửi đi trong `qr_codes`, cùng cách
  // `addComboToCart` làm. Tra theo `variant_id` phụ thuộc vào giả định BE trả
  // đúng id mà định nghĩa combo đang giữ.
  const byCode = new Map(details.map((detail) => [detail.variant.variant_code, detail]));

  const added: OrderItem[] = deficits.map((deficit) => {
    const detail = byCode.get(deficit.variantCode);
    if (!detail) {
      throw new Error('Không lấy được thông tin sản phẩm để tăng số combo');
    }
    const base = variantToOrderItem(mapDetailToPosVariant(detail), fulfillmentMode, newId);
    const warehouseId = resolveWarehouseId(items, comboGroupId, deficit.variantId);
    return recalcItem({ ...base, quantity: deficit.deficit, warehouseId }, fulfillmentMode);
  });

  return mergeLooseDuplicates([...items, ...added], fulfillmentMode);
}
